import { inject, Injectable } from "@angular/core";
import dayjs from "dayjs";
import { RegionService } from "src/app/services/feature-access-control/region.service";
import { dateUtil } from "src/app/support/date-util";

@Injectable()
export class FormatterService {
  readonly #regionService = inject(RegionService);

  readonly #mapNumberFormatters = new Map<string, Intl.NumberFormat>();

  number({
    value,
    minimumFractionDigits,
    maximumFractionDigits
  }: {
    value: number | undefined;
    minimumFractionDigits: number;
    maximumFractionDigits: number;
  }): string {
    if (value === undefined) {
      return "";
    }

    const locale = this.#regionService.getLocale();

    const key = this.#buildKey({
      locale,
      style: "decimal",
      minimumFractionDigits,
      maximumFractionDigits
    });

    let cachedFormatter = this.#mapNumberFormatters.get(key);

    if (!cachedFormatter) {
      cachedFormatter = new Intl.NumberFormat(locale, {
        style: "decimal",
        minimumFractionDigits,
        maximumFractionDigits
      });

      this.#mapNumberFormatters.set(key, cachedFormatter);
    }

    return cachedFormatter.format(value);
  }

  duration({
    value,
    withSuffix = true,
    units = "seconds",
    disableHumanize
  }: {
    value: string | number | undefined;
    withSuffix?: boolean;
    units?: "seconds" | "minutes" | "months";
    disableHumanize?: boolean;
  }): string {
    if (value === undefined || value === null) {
      return "";
    }

    if (!disableHumanize) {
      return dayjs
        .duration(parseInt(value.toString()), units)
        .humanize(withSuffix);
    }

    const duration = dayjs.duration(parseInt(value.toString()), units);

    switch (true) {
      case duration.days() > 0: {
        return duration.format(`D[d] H[h] m[m]`);
      }
      case duration.hours() > 0: {
        return duration.format(`H[h] m[m]`);
      }
      default: {
        return duration.format("m[m]");
      }
    }
  }

  durationFromNow({
    value,
    withSuffix = true,
    disableHumanize
  }: {
    value: string | number | undefined;
    withSuffix?: boolean;
    disableHumanize?: boolean;
  }): string {
    if (!disableHumanize) {
      return dayjs
        .duration(dateUtil(value).diff(dateUtil(), "seconds"), "seconds")
        .locale(this.#regionService.getLocale())
        .humanize(withSuffix);
    }

    return `${dayjs
      .duration(dateUtil(value).diff(dateUtil(), "seconds"), "seconds")
      .locale(this.#regionService.getLocale())
      .format("m")} min`;
  }

  #buildKey({
    locale,
    style,
    minimumFractionDigits,
    maximumFractionDigits
  }: {
    locale: string;
    style: "decimal" | "percent";
    minimumFractionDigits: number;
    maximumFractionDigits: number;
  }): string {
    return `${locale}-${style}-${minimumFractionDigits}-${maximumFractionDigits}`;
  }
}
